"use client";

import React, { useState, useEffect } from "react";
import {
  Calculator,
  ArrowRight,
  Activity,
  Percent,
  DollarSign,
  ShieldCheck,
} from "lucide-react";

function AnimatedNumber({
  value,
  formatString = "currency",
}: {
  value: number;
  formatString?: "currency" | "percent" | "number";
}) {
  const [displayValue, setDisplayValue] = useState(value);

  const formatValue = (v: number) => {
    if (formatString === "percent") {
      return new Intl.NumberFormat("en-US", {
        style: "percent",
        maximumFractionDigits: 2,
      }).format(v / 100);
    } else if (formatString === "number") {
      return new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 0,
      }).format(v);
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(v);
  };

  useEffect(() => {
    let startTimestamp: number | null = null;
    const duration = 400; // ms

    // Store exact start value to calculate delta from
    const startValue = displayValue;
    const endValue = value;

    if (startValue === endValue) return;

    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;

      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);

      setDisplayValue(startValue + (endValue - startValue) * easeOutQuart);

      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        setDisplayValue(endValue);
      }
    };

    window.requestAnimationFrame(step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return <>{formatValue(displayValue)}</>;
}

export function DilutionCalculator({
  initialSpacData,
}: {
  initialSpacData?: {
    trustInitialAmount?: string | number;
    sponsorPromote?: string;
    warrantFractionPerUnit?: string | number;
  } | null;
}) {
  // Core Inputs
  const [preMergerValuation, setPreMergerValuation] =
    useState<number>(1200000000);
  const [ipoSize, setIpoSize] = useState<number>(
    initialSpacData?.trustInitialAmount
      ? Number(initialSpacData.trustInitialAmount)
      : 345000000,
  );
  const [redemptionsPct, setRedemptionsPct] = useState<number>(0.5);
  const [pipeInvestment, setPipeInvestment] = useState<number>(150000000);

  // Advanced Settings
  let defaultWarrants = 0.3333333333;
  if (
    initialSpacData?.warrantFractionPerUnit !== undefined &&
    initialSpacData?.warrantFractionPerUnit !== null
  ) {
    if (typeof initialSpacData.warrantFractionPerUnit === "number") {
      defaultWarrants = initialSpacData.warrantFractionPerUnit;
    } else if (typeof initialSpacData.warrantFractionPerUnit === "string") {
      const strVal = (
        initialSpacData.warrantFractionPerUnit as string
      ).toLowerCase();
      if (strVal.includes("1/3")) defaultWarrants = 0.3333333333;
      else if (strVal.includes("1/4")) defaultWarrants = 0.25;
      else if (strVal === "1") defaultWarrants = 1.0;
      else if (strVal === "0" || strVal === "none") defaultWarrants = 0;
    }
  }

  const [sponsorPromotePct, setSponsorPromotePct] = useState<number>(
    initialSpacData?.sponsorPromote
      ? parseFloat(initialSpacData.sponsorPromote.replace("%", "")) / 100 || 0.2
      : 0.2,
  );
  const [warrantsPerUnit, setWarrantsPerUnit] =
    useState<number>(defaultWarrants);
  const [esopPoolPct, setEsopPoolPct] = useState<number>(0.05);
  const [rightsRatio, setRightsRatio] = useState<number>(0); // 0:0 by default (Meshflow style)

  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [isMounted, setIsMounted] = useState(false);

  // Load from local storage on mount
  useEffect(() => {
    setIsMounted(true);
    // Only load from cache if we aren't overriding with specific ticker data
    if (!initialSpacData) {
      const saved = localStorage.getItem("meshflow_dilution_full_state");
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed.preMergerValuation)
            setPreMergerValuation(parsed.preMergerValuation);
          if (parsed.ipoSize) setIpoSize(parsed.ipoSize);
          if (parsed.redemptionsPct !== undefined)
            setRedemptionsPct(parsed.redemptionsPct);
          if (parsed.pipeInvestment !== undefined)
            setPipeInvestment(parsed.pipeInvestment);
          if (parsed.sponsorPromotePct !== undefined)
            setSponsorPromotePct(parsed.sponsorPromotePct);
          if (parsed.warrantsPerUnit !== undefined)
            setWarrantsPerUnit(parsed.warrantsPerUnit);
          if (parsed.esopPoolPct !== undefined)
            setEsopPoolPct(parsed.esopPoolPct);
          if (parsed.rightsRatio !== undefined)
            setRightsRatio(parsed.rightsRatio);
        } catch (error) {
          console.error("Failed to parse cached calculator state", error);
        }
      }
    }
  }, [initialSpacData]);

  // Save to local storage on change
  useEffect(() => {
    if (isMounted) {
      localStorage.setItem(
        "meshflow_dilution_full_state",
        JSON.stringify({
          preMergerValuation,
          ipoSize,
          redemptionsPct,
          pipeInvestment,
          sponsorPromotePct,
          warrantsPerUnit,
          esopPoolPct,
          rightsRatio,
        }),
      );
    }
  }, [
    preMergerValuation,
    ipoSize,
    redemptionsPct,
    pipeInvestment,
    sponsorPromotePct,
    warrantsPerUnit,
    esopPoolPct,
    rightsRatio,
    isMounted,
  ]);

  // Client-side execution (instant)
  const calculateDilution = () => {
    const ipo = Number(ipoSize);
    const redemptions = Number(redemptionsPct);
    const targetEV = Number(preMergerValuation);
    const pipe = Number(pipeInvestment);
    const promotePct = Number(sponsorPromotePct);
    const warrantRatio = Number(warrantsPerUnit);
    const esopPct = Number(esopPoolPct);
    const rRatio = Number(rightsRatio);
    const ppw = 11500000; // Meshflow private placement warrants

    if (
      !ipo ||
      ipo <= 0 ||
      isNaN(ipo) ||
      !targetEV ||
      targetEV <= 0 ||
      isNaN(targetEV)
    ) {
      return null;
    }

    const IPO_ISSUE_PRICE = 10.0;
    const publicSharesIssued = ipo / IPO_ISSUE_PRICE;
    const publicShares = publicSharesIssued * (1 - redemptions);

    const cash = publicShares * IPO_ISSUE_PRICE;
    const trustValuePerShare = publicShares > 0 ? cash / publicShares : 0;

    const targetShares =
      trustValuePerShare > 0 ? targetEV / trustValuePerShare : 0;
    const pipeShares = trustValuePerShare > 0 ? pipe / trustValuePerShare : 0;

    const promoteMultiplier = promotePct / (1 - promotePct);
    const sponsorShares = publicShares * promoteMultiplier;

    const publicWarrants = publicSharesIssued * warrantRatio;
    const totalWarrants = publicWarrants + ppw;

    const rightsShares = rRatio > 0 ? publicSharesIssued / rRatio : 0;

    const postMergerSharesPreEsop =
      publicShares + sponsorShares + targetShares + pipeShares + rightsShares;

    const esopShares = postMergerSharesPreEsop * esopPct;
    const postMergerShares = postMergerSharesPreEsop;
    const fullyDilutedShares = postMergerShares + totalWarrants + esopShares;

    const ownership = {
      public: postMergerShares > 0 ? publicShares / postMergerShares : 0,
      sponsor: postMergerShares > 0 ? sponsorShares / postMergerShares : 0,
      target: postMergerShares > 0 ? targetShares / postMergerShares : 0,
      pipe: postMergerShares > 0 ? pipeShares / postMergerShares : 0,
      rights:
        postMergerShares > 0 && rightsShares > 0
          ? rightsShares / postMergerShares
          : 0,
    };

    const sponsorDilution =
      postMergerShares > 0 ? sponsorShares / postMergerShares : 0;
    const pipeDilution =
      postMergerShares > 0 ? pipeShares / postMergerShares : 0;
    const warrantOverhang =
      postMergerShares + totalWarrants > 0
        ? totalWarrants / (postMergerShares + totalWarrants)
        : 0;

    const totalDilution =
      fullyDilutedShares > 0 ? 1 - targetShares / fullyDilutedShares : 0;
    const cashContributionEffect = targetEV > 0 ? (cash + pipe) / targetEV : 0;
    const effectiveDilution = totalDilution - cashContributionEffect;

    const enterpriseValue = targetEV + cash + pipe;
    const proFormaSharePrice =
      postMergerShares > 0 ? enterpriseValue / postMergerShares : 0;

    return {
      trustValuePerShare,
      targetShares,
      pipeShares,
      sponsorShares,
      publicShares,
      publicSharesIssued,
      publicWarrants,
      privatePlacementWarrants: ppw,
      totalWarrants,
      rightsShares,
      postMergerShares,
      esopShares,
      fullyDilutedShares,
      ownership,
      sponsorDilution,
      pipeDilution,
      warrantOverhang,
      totalDilution,
      cashContributionEffect,
      effectiveDilution,
      enterpriseValue,
      proFormaSharePrice,
      cashInTrust: cash,
      totalCash: cash + pipe,
    };
  };

  const results = calculateDilution();

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(val);
  };

  const formatCurrencyDetailed = (val: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val);
  };

  const formatPercent = (val: number, decimals: number = 1) => {
    return new Intl.NumberFormat("en-US", {
      style: "percent",
      maximumFractionDigits: decimals,
      minimumFractionDigits: decimals,
    }).format(val);
  };

  const formatNumber = (val: number) => {
    return new Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(val);
  };

  return (
    <div className="w-full max-w-6xl mx-auto mt-6 mb-12 font-sans text-foreground space-y-6">
      {/* Top Hero & Inputs Area */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Left: Main Outputs (Hero) */}
        <div className="lg:col-span-7 rounded-3xl border border-border bg-background p-4 sm:p-6 lg:p-8 flex flex-col justify-between shadow-xl relative overflow-hidden group">
          <div className="absolute inset-0 bg-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>

          <div className="flex items-center gap-x-3 mb-6">
            <div className="h-9 w-9 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Calculator className="h-4 w-4 text-blue-400" />
            </div>
            <h2 className="text-lg lg:text-xl font-bold tracking-tight">
              Cap Table & Dilution Overview
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 z-10 lg:mt-4">
            <div>
              <h3 className="flex items-center gap-x-2 text-xs font-semibold text-muted-foreground tracking-widest uppercase mb-1.5">
                Effective Dilution
                <span className="group/help relative cursor-help">
                  <div className="h-3.5 w-3.5 rounded-full border border-muted-foreground/30 flex items-center justify-center text-[9px] text-muted-foreground">
                    ?
                  </div>
                  <div className="absolute bottom-full mb-2 w-56 bg-foreground text-background text-xs p-3 rounded-xl shadow-xl opacity-0 invisible group-hover/help:opacity-100 group-hover/help:visible transition-all z-10 font-normal normal-case tracking-normal">
                    Total dilution experienced by target shareholders, net of
                    capital contributed through SPAC trust and PIPE financing.
                  </div>
                </span>
              </h3>
              <p className="text-4xl sm:text-5xl lg:text-6xl font-black tabular-nums tracking-tighter text-blue-500">
                {results ? (
                  <AnimatedNumber
                    value={results.effectiveDilution * 100}
                    formatString="percent"
                  />
                ) : (
                  "0%"
                )}
              </p>
              <div className="text-xs font-medium text-muted-foreground mt-3 space-y-1">
                <div className="flex justify-between">
                  <span>Total Target Dilution:</span>
                  <span className="text-foreground font-semibold">
                    {results ? formatPercent(results.totalDilution) : "0%"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Cash Contribution Effect:</span>
                  <span className="text-emerald-500 font-semibold">
                    -
                    {results
                      ? formatPercent(results.cashContributionEffect)
                      : "0%"}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-center border-t md:border-t-0 md:border-l border-border pt-4 md:pt-0 md:pl-6">
              <h3 className="flex items-center gap-x-2 text-xs font-semibold text-muted-foreground tracking-widest uppercase mb-1.5">
                Pro Forma Share Price
                <span className="group/help relative cursor-help">
                  <div className="h-3.5 w-3.5 rounded-full border border-muted-foreground/30 flex items-center justify-center text-[9px] text-muted-foreground">
                    ?
                  </div>
                  <div className="absolute bottom-full mb-2 w-56 bg-foreground text-background text-xs p-3 rounded-xl shadow-xl opacity-0 invisible group-hover/help:opacity-100 group-hover/help:visible transition-all z-10 font-normal normal-case tracking-normal">
                    Enterprise value (Target + SPAC Cash + PIPE) divided by
                    Post-Merger Shares.
                  </div>
                </span>
              </h3>
              <p className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tabular-nums tracking-tight break-words">
                {results
                  ? formatCurrencyDetailed(results.proFormaSharePrice)
                  : "$0.00"}
              </p>
              <p className="text-xs font-medium text-muted-foreground mt-3">
                Trust Value:{" "}
                <span className="text-foreground font-semibold">
                  {results
                    ? formatCurrencyDetailed(results.trustValuePerShare)
                    : "$0.00"}
                </span>
              </p>
            </div>

            {/* NEW: Ownership Chart in Hero */}
            <div className="md:col-span-2 pt-6 border-t border-border mt-2">
              <h3 className="text-xs font-semibold text-muted-foreground tracking-widest uppercase mb-4">
                Post-Merger Ownership
              </h3>
              {results ? (
                <>
                  <div className="w-full flex h-4 rounded-full overflow-hidden ring-1 ring-border shadow-inner mb-4">
                    <div
                      style={{ width: `${results.ownership.target * 100}%` }}
                      className="bg-blue-500 h-full transition-all duration-500"
                      title="Target Equity"
                    ></div>
                    <div
                      style={{ width: `${results.ownership.public * 100}%` }}
                      className="bg-emerald-400 h-full transition-all duration-500"
                      title="Public Shareholders"
                    ></div>
                    <div
                      style={{ width: `${results.ownership.sponsor * 100}%` }}
                      className="bg-indigo-500 h-full transition-all duration-500"
                      title="Sponsor Promote"
                    ></div>
                    {results.ownership.pipe > 0 && (
                      <div
                        style={{ width: `${results.ownership.pipe * 100}%` }}
                        className="bg-amber-500 h-full transition-all duration-500"
                        title="PIPE Investors"
                      ></div>
                    )}
                    {results.ownership.rights > 0 && (
                      <div
                        style={{ width: `${results.ownership.rights * 100}%` }}
                        className="bg-red-500 h-full transition-all duration-500"
                        title="Legacy Rights Holders"
                      ></div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-2 text-[10px] font-semibold">
                    <div className="flex items-center gap-x-1.5 text-muted-foreground">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>{" "}
                      Target: {formatPercent(results.ownership.target)}
                    </div>
                    <div className="flex items-center gap-x-1.5 text-muted-foreground">
                      <div className="w-2 h-2 rounded-full bg-emerald-400"></div>{" "}
                      Public: {formatPercent(results.ownership.public)}
                    </div>
                    <div className="flex items-center gap-x-1.5 text-muted-foreground">
                      <div className="w-2 h-2 rounded-full bg-indigo-500"></div>{" "}
                      Sponsor: {formatPercent(results.ownership.sponsor)}
                    </div>
                    {results.ownership.pipe > 0 && (
                      <div className="flex items-center gap-x-1.5 text-muted-foreground">
                        <div className="w-2 h-2 rounded-full bg-amber-500"></div>{" "}
                        PIPE: {formatPercent(results.ownership.pipe)}
                      </div>
                    )}
                    {results.ownership.rights > 0 && (
                      <div className="flex items-center gap-x-1.5 text-red-500">
                        <div className="w-2 h-2 rounded-full bg-red-500"></div>{" "}
                        Rights: {formatPercent(results.ownership.rights)}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="w-full h-4 bg-muted rounded-full"></div>
              )}
            </div>
          </div>
        </div>

        {/* Right: Basic Inputs */}
        <div className="lg:col-span-5 rounded-3xl bg-muted/20 border border-border p-4 sm:p-6 shadow-xl flex flex-col justify-center transition-all duration-500 h-full relative">
          <div className="space-y-8 z-10 [&_label]:flex [&_label]:items-center [&_label]:justify-between [&_label]:text-xs [&_label]:font-semibold [&_label]:mb-3 [&_label>span]:px-2 [&_label>span]:py-0.5 [&_label>span]:bg-background [&_label>span]:rounded-md [&_label>span]:border [&_label>span]:border-border [&_label>span]:font-bold [&_input[type='range']]:w-full [&_input[type='range']]:h-1.5 [&_input[type='range']]:bg-muted [&_input[type='range']]:rounded-lg [&_input[type='range']]:appearance-none [&_input[type='range']]:cursor-pointer">
            {/* Target EV Input */}
            <div>
              <label htmlFor="preMergerValuation">
                Pre-Merger Valuation
                <span className="text-blue-500">
                  {formatCurrency(preMergerValuation)}
                </span>
              </label>
              <input
                id="preMergerValuation"
                type="range"
                min={100000000}
                max={3000000000}
                step={50000000}
                value={preMergerValuation}
                onChange={(e) => setPreMergerValuation(Number(e.target.value))}
                className="accent-blue-500"
              />
            </div>

            {/* SPAC IPO Proceeds Input */}
            <div>
              <label htmlFor="ipoSize">
                SPAC IPO Size
                <span className="text-blue-500">{formatCurrency(ipoSize)}</span>
              </label>
              <input
                id="ipoSize"
                type="range"
                min={50000000}
                max={1000000000}
                step={10000000}
                value={ipoSize}
                onChange={(e) => setIpoSize(Number(e.target.value))}
                className="accent-blue-500"
              />
            </div>

            {/* Redemptions Input */}
            <div>
              <label htmlFor="redemptionsPct">
                Trust Redemptions (Heat)
                <span className="text-amber-500">
                  {(redemptionsPct * 100).toFixed(0)}%
                </span>
              </label>
              <input
                id="redemptionsPct"
                type="range"
                min={0}
                max={100}
                step={5}
                value={(redemptionsPct * 100).toFixed(0)}
                onChange={(e) =>
                  setRedemptionsPct(Number(e.target.value) / 100)
                }
                className="accent-amber-500"
              />
            </div>

            {/* PIPE Input */}
            <div>
              <label htmlFor="pipeInvestment">
                PIPE Investment
                <span className="text-amber-500">
                  {formatCurrency(pipeInvestment)}
                </span>
              </label>
              <input
                id="pipeInvestment"
                type="range"
                min={0}
                max={500000000}
                step={5000000}
                value={pipeInvestment}
                onChange={(e) => setPipeInvestment(Number(e.target.value))}
                className="accent-amber-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Breakdown Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
        {/* Post-Merger Shares */}
        <div className="col-span-1 lg:col-span-2 rounded-3xl border border-border bg-muted/10 p-4 sm:p-5 flex flex-col justify-between hover:bg-muted/20 transition-colors duration-300 shadow-sm relative group overflow-hidden">
          <div className="flex items-center gap-x-2 text-muted-foreground mb-3">
            <Activity className="h-4 w-4 text-emerald-500/70" />
            <span className="text-[11px] font-bold uppercase tracking-widest">
              Total Output Structure
            </span>
          </div>
          <div>
            <p className="text-3xl font-extrabold tabular-nums tracking-tight">
              {results ? (
                <AnimatedNumber
                  value={results.fullyDilutedShares / 1000000}
                  formatString="number"
                />
              ) : (
                "0"
              )}{" "}
              <span className="text-base font-semibold text-muted-foreground">
                M
              </span>
            </p>
            <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mt-1">
              Fully Diluted Shares
            </p>
          </div>
          <div className="mt-5 pt-3 border-t border-border/50 text-[11px] uppercase tracking-wide font-bold space-y-1.5">
            <div className="flex justify-between items-center text-muted-foreground">
              <span>Post-Merger Shares:</span>{" "}
              <span className="text-foreground">
                {results ? formatNumber(results.postMergerShares) : "0"}
              </span>
            </div>
            <div className="flex justify-between items-center text-muted-foreground">
              <span>Target Shares Issued:</span>{" "}
              <span className="text-foreground">
                {results ? formatNumber(results.targetShares) : "0"}
              </span>
            </div>
            <div className="flex justify-between items-center text-muted-foreground">
              <span>Public SPAC Shares:</span>{" "}
              <span className="text-foreground">
                {results ? formatNumber(results.publicShares) : "0"}
              </span>
            </div>
          </div>
        </div>

        {/* Sponsor Promote */}
        <div className="col-span-1 lg:col-span-1 rounded-3xl border border-border bg-muted/10 p-4 sm:p-5 flex flex-col hover:bg-muted/20 transition-colors duration-300 shadow-sm">
          <div className="flex items-center text-muted-foreground mb-3">
            <span className="text-[11px] font-bold uppercase tracking-widest">
              Sponsor Promote
            </span>
          </div>
          <p className="text-2xl font-extrabold tabular-nums tracking-tight text-indigo-400">
            {results ? formatNumber(results.sponsorShares) : "0"}{" "}
            <span className="text-xs font-semibold">sh</span>
          </p>

          <div className="mt-auto pt-3 border-t border-border/50">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-0.5 block">
              Dilution Impact
            </span>
            <p className="text-lg font-black tracking-tight">
              {results ? formatPercent(results.sponsorDilution) : "0%"}
            </p>
          </div>
        </div>

        {/* PIPE Investment */}
        <div className="col-span-1 lg:col-span-1 rounded-3xl border border-border bg-muted/10 p-4 sm:p-5 flex flex-col hover:bg-muted/20 transition-colors duration-300 shadow-sm">
          <div className="flex items-center text-muted-foreground mb-3">
            <span className="text-[11px] font-bold uppercase tracking-widest">
              PIPE Impact
            </span>
          </div>
          <p className="text-2xl font-extrabold tabular-nums tracking-tight text-amber-500">
            {results ? formatNumber(results.pipeShares) : "0"}{" "}
            <span className="text-xs font-semibold">sh</span>
          </p>

          <div className="mt-auto pt-3 border-t border-border/50">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-0.5 block">
              Dilution Impact
            </span>
            <p className="text-lg font-black tracking-tight">
              {results ? formatPercent(results.pipeDilution) : "0%"}
            </p>
          </div>
        </div>

        {/* Warrant Overhang */}
        <div className="col-span-1 lg:col-span-1 rounded-3xl border border-border bg-muted/10 p-4 sm:p-5 flex flex-col hover:bg-muted/20 transition-colors duration-300 shadow-sm relative group overflow-hidden">
          <div className="flex items-center justify-between text-muted-foreground mb-3">
            <span className="text-[11px] font-bold uppercase tracking-widest">
              Warrant Overhang
            </span>
            <ShieldCheck className="h-4 w-4 text-blue-500/70" />
          </div>
          <p className="text-2xl font-extrabold tabular-nums tracking-tight">
            {results ? formatNumber(results.totalWarrants) : "0"}{" "}
            <span className="text-xs font-semibold text-muted-foreground">
              Total
            </span>
          </p>

          <div className="mt-auto pt-3 border-t border-border/50">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-0.5 block">
              Dilution Impact
            </span>
            <p className="text-lg font-black tracking-tight">
              {results ? formatPercent(results.warrantOverhang) : "0%"}
            </p>
          </div>
        </div>
      </div>

      {/* Advanced Parameters Section */}
      <div className="rounded-3xl border border-border bg-muted/5 overflow-hidden shadow-sm mt-8">
        {/* Advanced Toggle Button */}
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full py-4 px-6 bg-muted/10 hover:bg-muted/20 transition-colors flex items-center justify-between text-sm font-semibold text-foreground border-b border-border/50 cursor-pointer"
        >
          <span className="flex items-center gap-x-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-muted-foreground"
            >
              <circle cx="12" cy="12" r="3"></circle>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
            </svg>
            Advanced Customization
          </span>
          <span className="text-muted-foreground flex items-center gap-x-1 border border-border px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold">
            {showAdvanced ? "Hide" : "Expand Parameters"}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`transition-transform duration-300 ml-1 ${showAdvanced ? "rotate-180" : ""}`}
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </span>
        </button>

        {/* Advanced Settings Container */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 gap-8 overflow-hidden transition-all duration-500 ease-in-out ${showAdvanced ? "max-h-[800px] opacity-100 p-4 sm:p-6 md:p-8" : "max-h-0 opacity-0 p-0 m-0 border-none pointer-events-none"}`}
        >
          {/* Column 1 */}
          <div className="space-y-6 [&_label]:flex [&_label]:items-center [&_label]:justify-between [&_label]:text-xs [&_label]:font-semibold [&_label]:mb-3 [&_label>span]:px-2 [&_label>span]:py-0.5 [&_label>span]:bg-background [&_label>span]:rounded-md [&_label>span]:border [&_label>span]:border-border [&_label>span]:font-bold [&_input[type='range']]:w-full [&_input[type='range']]:h-1.5 [&_input[type='range']]:bg-muted [&_input[type='range']]:rounded-lg [&_input[type='range']]:appearance-none [&_input[type='range']]:cursor-pointer">
            {/* ESOP Input */}
            <div>
              <label htmlFor="esopPoolPct">
                Incentive Pool (ESOP)
                <span className="text-indigo-400">
                  {(esopPoolPct * 100).toFixed(0)}%
                </span>
              </label>
              <input
                id="esopPoolPct"
                type="range"
                min={0}
                max={20}
                step={1}
                value={(esopPoolPct * 100).toFixed(0)}
                onChange={(e) => setEsopPoolPct(Number(e.target.value) / 100)}
                className="accent-indigo-400"
              />
            </div>

            {/* Sponsor Promote */}
            <div>
              <label
                htmlFor="sponsorPromotePct"
                className="text-[10px] font-bold text-muted-foreground tracking-widest uppercase mb-1.5 block"
              >
                Sponsor Promote Structure
              </label>
              <select
                id="sponsorPromotePct"
                value={sponsorPromotePct}
                onChange={(e) => setSponsorPromotePct(Number(e.target.value))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm font-semibold focus:ring-2 focus:ring-blue-500 outline-none"
              >
                <option value={0.2}>20% Standard Promote</option>
                <option value={0.25}>25% Premium Promote</option>
                <option value={0.1}>10% Reduced Promote</option>
              </select>
            </div>
          </div>

          {/* Column 2 */}
          <div className="space-y-6 [&_label]:flex [&_label]:items-center [&_label]:justify-between [&_label]:text-xs [&_label]:font-semibold [&_label]:mb-3 [&_label>span]:px-2 [&_label>span]:py-0.5 [&_label>span]:bg-background [&_label>span]:rounded-md [&_label>span]:border [&_label>span]:border-border [&_label>span]:font-bold [&_input[type='range']]:w-full [&_input[type='range']]:h-1.5 [&_input[type='range']]:bg-muted [&_input[type='range']]:rounded-lg [&_input[type='range']]:appearance-none [&_input[type='range']]:cursor-pointer">
            {/* Warrants Ratio */}
            <div>
              <label
                htmlFor="warrantsPerUnit"
                className="text-[10px] font-bold text-muted-foreground tracking-widest uppercase mb-1.5 block"
              >
                IPO Warrant Coverage
              </label>
              <select
                id="warrantsPerUnit"
                value={warrantsPerUnit}
                onChange={(e) => setWarrantsPerUnit(Number(e.target.value))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm font-semibold focus:ring-2 focus:ring-blue-500 outline-none"
              >
                <option value={0.5}>1/2 per Unit (Legacy)</option>
                <option value={0.3333333333}>1/3 per Unit (Meshflow)</option>
                <option value={1.0}>1/1 per Unit (Full)</option>
                <option value={0}>No Warrants</option>
              </select>
            </div>

            {/* Rights Ratio */}
            <div>
              <label
                htmlFor="rightsRatio"
                className="text-[10px] font-bold text-muted-foreground tracking-widest uppercase mb-1.5 block"
              >
                Legacy Rights Conversion Ratio
              </label>
              <select
                id="rightsRatio"
                value={rightsRatio}
                onChange={(e) => setRightsRatio(Number(e.target.value))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm font-semibold focus:ring-2 focus:ring-blue-500 outline-none"
              >
                <option value={0}>0:0 (No Rights - Meshflow)</option>
                <option value={10}>10:1 (Legacy Standard)</option>
                <option value={4}>4:1 (Legacy Structural)</option>
                <option value={3}>3:1 (One share / 3 rights)</option>
                <option value={2}>2:1 (One share / 2 rights)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Conversion Section: Meshflow VS Legacy */}
      <div className="rounded-3xl border border-border bg-background p-6 sm:p-8 md:p-12 shadow-xl relative overflow-hidden mt-16">
        <div className="absolute top-0 right-0 w-80 h-80 bg-blue-500/5 blur-3xl rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-500/5 blur-3xl rounded-full translate-y-1/2 -translate-x-1/2"></div>

        <div className="max-w-3xl text-center mx-auto mb-12 relative z-10">
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-4 text-foreground">
            The Meshflow Frictionless Advantage
          </h2>
          <p className="text-lg text-muted-foreground">
            Here is how Meshflow's streamlined structure translates directly
            into preserved equity value for Target Shareholders.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10">
          {/* Standard Legacy SPAC Card */}
          <div className="rounded-3xl bg-background border border-border/60 p-8 flex flex-col shadow-sm">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-xl font-bold text-muted-foreground">
                Legacy SPAC
              </h3>
              <span className="bg-muted px-2 py-1 rounded-md text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                Standard Terms
              </span>
            </div>

            <ul className="space-y-5 mb-8 flex-grow">
              <li className="flex justify-between items-center">
                <span className="text-sm font-medium text-muted-foreground">
                  Warrant Coverage
                </span>
                <strong className="text-sm font-semibold text-foreground/80">
                  1:1 Warrants per Unit
                </strong>
              </li>
              <li className="flex justify-between items-center">
                <span className="text-sm font-medium text-muted-foreground">
                  Legacy Rights (10:1)
                </span>
                <strong className="text-sm font-semibold text-foreground/80">
                  Yields Instant Dilution
                </strong>
              </li>
            </ul>
            <div className="pt-6 border-t border-border/50">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 font-medium">
                Standard Market Friction
              </p>
              {results && (
                <div className="flex flex-col">
                  <p className="text-3xl font-bold text-foreground/40 line-through decoration-red-500/40">
                    {formatCurrency(
                      (results.publicSharesIssued / 10) * 10 +
                        results.publicSharesIssued * 1 * 1.5,
                    )}
                  </p>
                  <span className="text-[11px] font-medium text-muted-foreground/60 mt-2 uppercase tracking-wide">
                    Est. value of 10:1 Rights & 1:1 Warrants
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Meshflow SPAC Card */}
          <div className="rounded-3xl bg-muted/5 border border-blue-500/20 p-8 flex flex-col relative overflow-hidden shadow-lg shadow-blue-500/5 ring-1 ring-blue-500/10">
            <div className="absolute top-0 left-0 w-full h-[6px] bg-blue-500"></div>
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-xl font-bold text-foreground">
                Meshflow Model
              </h3>
              <span className="bg-blue-600 text-white px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest shadow-sm">
                Optimized
              </span>
            </div>

            <ul className="space-y-5 mb-8 flex-grow">
              <li className="flex justify-between items-center">
                <span className="text-sm font-medium text-muted-foreground">
                  Warrant Coverage
                </span>
                <strong className="text-sm font-semibold text-emerald-500 flex items-center gap-x-1.5">
                  <ShieldCheck className="w-4 h-4" /> 1/3 per Unit
                </strong>
              </li>
              <li className="flex justify-between items-center">
                <span className="text-sm font-medium text-muted-foreground">
                  Legacy Rights Deals
                </span>
                <strong className="text-sm font-semibold text-emerald-500 flex items-center gap-x-1.5">
                  <ShieldCheck className="w-4 h-4" /> Zero (0:0)
                </strong>
              </li>
              <li className="flex justify-between items-center">
                <span className="text-sm font-medium text-muted-foreground">
                  Post-IPO Flexibility
                </span>
                <strong className="text-sm font-semibold text-blue-500 flex items-center gap-x-1.5">
                  <Activity className="w-4 h-4" /> Maximum Retention
                </strong>
              </li>
            </ul>

            <div className="pt-6 border-t border-blue-500/20">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3 font-semibold">
                Pure Savings Over Legacy Standard
              </p>
              {results ? (
                <div className="flex flex-col">
                  <p className="text-5xl lg:text-6xl font-black text-blue-500 tracking-tighter mb-4 drop-shadow-sm">
                    <AnimatedNumber
                      value={
                        (results.publicSharesIssued / 10) * 10 +
                        (results.publicSharesIssued * 1 -
                          results.publicSharesIssued / 3) *
                          1.5
                      }
                      formatString="currency"
                    />
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 px-2.5 py-1 rounded inline-flex items-center text-xs font-bold shadow-sm">
                      <AnimatedNumber
                        value={results.publicSharesIssued / 10}
                        formatString="number"
                      />{" "}
                      <span className="ml-1 uppercase tracking-wider opacity-80">
                        Shares
                      </span>
                    </span>
                    <span className="text-muted-foreground/30 font-black">
                      +
                    </span>
                    <span className="bg-blue-500/10 border border-blue-500/20 text-blue-600 px-2.5 py-1 rounded inline-flex items-center text-xs font-bold shadow-sm">
                      <AnimatedNumber
                        value={
                          results.publicSharesIssued * 1 -
                          results.publicSharesIssued / 3
                        }
                        formatString="number"
                      />{" "}
                      <span className="ml-1 uppercase tracking-wider opacity-80">
                        Warrants
                      </span>
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-5xl font-black text-blue-500">...</p>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Disclaimer */}
      <div className="mt-12 text-xs text-muted-foreground/80 leading-relaxed max-w-4xl mx-auto text-center border-t border-border/50 pt-8">
        <p>
          <strong>Disclaimer:</strong> This tool is for informational and
          educational purposes only and does not constitute financial,
          investment, legal, or tax advice. The outputs generated are estimates
          based on specific assumptions and mathematical models. Actual
          transaction structures, dilution effects, and financial returns may
          vary significantly. Please consult with appropriate professional
          advisors before making any business decisions.
        </p>
      </div>
    </div>
  );
}
